import React from "react";
import {
  Phone,
  PhoneCall,
  CalendarCheck,
  CheckCircle2,
  MessageSquare,
  Mic,
  Clock,
  Headphones,
  Sparkles,
} from "lucide-react";

const Benefit = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-3 p-3 rounded-xl border border-white/5 bg-white/[0.015]">
    <div className="w-8 h-8 rounded-lg bg-[#39FF14]/10 border border-[#39FF14]/25 flex items-center justify-center text-[#39FF14]">
      <Icon className="w-4 h-4" />
    </div>
    <span className="text-sm text-zinc-200">{label}</span>
  </div>
);

const PhoneMockup = () => (
  <div className="relative mx-auto w-[290px] sm:w-[320px]" data-testid="ai-phone-mockup">
    <div className="absolute -inset-10 rounded-[3rem] bg-[#39FF14]/15 blur-[80px]" />
    <div className="absolute -inset-10 -translate-x-12 rounded-[3rem] bg-[#007AFF]/20 blur-[100px]" />
    <div className="relative phone-frame p-3">
      {/* Notch */}
      <div className="mx-auto mt-1 mb-3 w-24 h-5 rounded-full bg-black border border-white/10" />

      {/* Screen */}
      <div className="rounded-3xl bg-[#070707] border border-white/5 overflow-hidden">
        {/* Status bar */}
        <div className="px-4 py-2 flex items-center justify-between text-[10px] font-mono text-zinc-400">
          <span>9:41</span>
          <span className="flex items-center gap-1">
            <span className="live-dot" />
            LIVE
          </span>
        </div>

        {/* Caller */}
        <div className="px-5 py-6 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Incoming Call
          </div>
          <div className="mt-3 text-xl font-semibold text-white">
            Jamie Rivera
          </div>
          <div className="text-xs text-zinc-500 font-mono mt-1">
            +1 214-555-0114
          </div>

          <div className="relative mx-auto mt-6 w-28 h-28 rounded-full bg-gradient-to-br from-[#39FF14] to-[#007AFF] flex items-center justify-center">
            <div className="absolute inset-1 rounded-full bg-[#070707] flex items-center justify-center">
              <Headphones className="w-10 h-10 text-[#39FF14]" />
            </div>
            <span className="absolute -inset-2 rounded-full border border-[#39FF14]/30 animate-pulse-glow" />
          </div>

          {/* Wave */}
          <div className="bar-set mt-6 inline-flex items-end">
            <span className="bar wave-bar" />
            <span className="bar wave-bar" />
            <span className="bar wave-bar" />
            <span className="bar wave-bar" />
            <span className="bar wave-bar" />
            <span className="bar wave-bar" />
            <span className="bar wave-bar" />
          </div>
          <div className="text-[10px] font-mono text-zinc-500 mt-2">
            AI Receptionist · Speaking
          </div>
        </div>

        {/* Chat bubbles */}
        <div className="px-4 pb-5 space-y-2">
          <div className="max-w-[80%] bg-white/[0.04] border border-white/5 rounded-2xl rounded-bl-md px-3 py-2 text-xs text-zinc-300">
            “Hi Jamie, this is Core. How can I help?”
          </div>
          <div className="max-w-[85%] ml-auto bg-[#39FF14]/12 border border-[#39FF14]/25 rounded-2xl rounded-br-md px-3 py-2 text-xs text-zinc-100">
            “I'd like to book a consultation for Tuesday.”
          </div>
          <div className="max-w-[80%] bg-white/[0.04] border border-white/5 rounded-2xl rounded-bl-md px-3 py-2 text-xs text-zinc-300">
            “Booked you for Tue 3:30 PM. Confirmation sent.”
            <span className="inline-block w-1.5 h-3 align-middle bg-[#39FF14] ml-1 animate-blink" />
          </div>
        </div>
      </div>
    </div>

    {/* Floating booking confirm */}
    <div className="hidden md:flex absolute -right-16 top-10 animate-float-medium">
      <div className="glass rounded-2xl p-3 w-48">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#39FF14]">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Booking Confirmed
        </div>
        <div className="mt-2 text-xs text-white">Tue · Mar 12 · 3:30 PM</div>
        <div className="text-[10px] text-zinc-500 font-mono mt-1">
          Synced → CRM · GCal
        </div>
      </div>
    </div>

    {/* Floating analytics */}
    <div className="hidden md:flex absolute -left-20 bottom-6 animate-float-slow">
      <div className="glass rounded-2xl p-3 w-44">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          Calls Handled · 24h
        </div>
        <div className="mt-1 text-lg font-mono text-white">128</div>
        <div className="mt-2 flex items-end gap-1 h-8">
          {[40, 55, 70, 50, 80, 60, 95].map((h, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm"
              style={{
                height: `${h}%`,
                background: "linear-gradient(180deg,#39FF14,#007AFF)",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default function FeaturedAI() {
  return (
    <section
      id="ai-system"
      className="relative section-padding"
      data-testid="featured-ai-section"
    >
      {/* Background image faded */}
      <div className="absolute inset-0 dot-bg opacity-40 pointer-events-none" />
      <div className="absolute left-1/2 top-20 w-[600px] h-[600px] -translate-x-1/2 bg-[#007AFF]/10 blur-[160px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-6 reveal">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-[#39FF14]">
            <Sparkles className="w-3.5 h-3.5" />
            Featured System
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
            The AI Receptionist <br />
            that <span className="gradient-text-green">never misses</span>.
          </h2>
          <p className="mt-6 text-lg text-zinc-400 leading-relaxed max-w-xl">
            A human-sounding AI agent that answers every call, qualifies leads,
            books appointments, and syncs everything into your CRM —
            automatically, 24/7.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            <Benefit icon={Clock} label="24/7 lead capture" />
            <Benefit icon={PhoneCall} label="Never miss calls" />
            <Benefit icon={CalendarCheck} label="Appointment booking" />
            <Benefit icon={Mic} label="Sounds human" />
            <Benefit icon={MessageSquare} label="Lead qualification" />
            <Benefit icon={CheckCircle2} label="Seamless integrations" />
          </div>

          {/* Demo number CTA */}
          <div
            className="mt-8 p-5 rounded-2xl glass-strong flex flex-col sm:flex-row sm:items-center gap-4"
            data-testid="featured-demo-card"
          >
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                Call the live demo
              </div>
              <a
                href="tel:2149838371"
                className="block mt-1 text-2xl md:text-3xl font-bold text-white font-mono hover:text-[#39FF14] transition-colors"
                data-testid="featured-ai-phone-number"
              >
                214-983-8371
              </a>
            </div>
            <a
              href="tel:2149838371"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#39FF14] text-black font-semibold hover:shadow-[0_0_28px_rgba(57,255,20,0.45)] hover:bg-white transition-all"
              data-testid="featured-ai-demo-button"
            >
              <Phone className="w-4 h-4" />
              Try The AI Receptionist
            </a>
          </div>
        </div>

        <div className="lg:col-span-6 flex justify-center reveal">
          <PhoneMockup />
        </div>
      </div>
    </section>
  );
}
